import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiGrid, FiList, FiExternalLink, FiRefreshCw, FiFilter } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const NFTGallery = ({ profile, theme }) => {
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedCollection, setSelectedCollection] = useState('all');
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    if (profile.wallet_address) {
      loadNFTs();
    }
  }, [profile.wallet_address]);

  const createNftPlaceholder = (text, bgColor) => {
    const encodedSvg = encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'>
        <rect width='100%' height='100%' fill='${bgColor}'/>
        <text x='50%' y='50%' fill='white' text-anchor='middle' dy='.3em' font-family='sans-serif' font-size='30'>${text}</text>
      </svg>`
    );
    return `data:image/svg+xml,${encodedSvg}`;
  };

  const loadNFTs = async () => {
    setLoading(true);
    try {
      // Mock NFT data - replace with actual API call to OpenSea, Alchemy, or similar
      const mockNFTs = [
        { id: '1', name: 'Cosmic Ape #1234', collection: 'Cosmic Apes', image: createNftPlaceholder('NFT 1', '#1a2b3c'), description: 'Rare cosmic ape with golden eyes', openseaUrl: '#', floorPrice: '0.5 ETH' },
        { id: '2', name: 'Digital Dreams #567', collection: 'Digital Dreams', image: createNftPlaceholder('NFT 2', '#4d5a6b'), description: 'Surreal digital art piece', openseaUrl: '#', floorPrice: '0.2 ETH' },
        { id: '3', name: 'Genesis Pass #88', collection: 'Genesis Pass', image: createNftPlaceholder('NFT 3', '#7e8a9b'), description: 'Exclusive community access pass', openseaUrl: '#', floorPrice: '1.0 ETH' },
        { id: '4', name: 'Pixel Punk #999', collection: 'Pixel Punks', image: createNftPlaceholder('NFT 4', '#a1b2c3'), description: 'Rare pixel punk with hat', openseaUrl: '#', floorPrice: '2.5 ETH' },
        { id: '5', name: 'Meta World #42', collection: 'Meta Worlds', image: createNftPlaceholder('NFT 5', '#d4e5f6'), description: 'Virtual land in metaverse', openseaUrl: '#', floorPrice: '0.8 ETH' },
        { id: '6', name: 'Abstract Art #777', collection: 'Abstract Art', image: createNftPlaceholder('NFT 6', '#f8e7d6'), description: 'Unique abstract composition', openseaUrl: '#', floorPrice: '0.3 ETH' }
      ];
      setNfts(mockNFTs);
      // Extract collections
      const uniqueCollections = [...new Set(mockNFTs.map(nft => nft.collection))];
      setCollections(uniqueCollections);
    } catch (error) {
      console.error('Error loading NFTs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredNfts = selectedCollection === 'all'
    ? nfts
    : nfts.filter(nft => nft.collection === selectedCollection);

  const renderGridView = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {filteredNfts.map((nft, index) => (
        <motion.div
          key={nft.id}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
          className={`rounded-xl overflow-hidden border ${theme.border} ${theme.bg} hover:shadow-lg transition-shadow`}
        >
          <div className="aspect-square bg-gray-100">
            <img src={nft.image} alt={nft.name} className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="p-4">
            <h4 className={`font-medium ${theme.text} mb-1`}>{nft.name}</h4>
            <p className={`text-xs ${theme.subtext} mb-2`}>{nft.collection}</p>
            <div className="flex items-center justify-between">
              <span className={`text-sm font-medium ${theme.accent}`}>
                {nft.floorPrice}
              </span>
              <a
                href={nft.openseaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-700"
              >
                <SafeIcon icon={FiExternalLink} className="text-sm" />
              </a>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  const renderListView = () => (
    <div className="space-y-4">
      {filteredNfts.map((nft, index) => (
        <motion.div
          key={nft.id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
          className={`flex items-center space-x-4 p-4 rounded-xl border ${theme.border} ${theme.bg} hover:shadow-md transition-shadow`}
        >
          <img src={nft.image} alt={nft.name} className="w-16 h-16 rounded-lg object-cover" />
          <div className="flex-1">
            <h4 className={`font-medium ${theme.text}`}>{nft.name}</h4>
            <p className={`text-sm ${theme.subtext}`}>{nft.collection}</p>
            <p className={`text-xs ${theme.subtext} mt-1`}>{nft.description}</p>
          </div>
          <div className="text-right">
            <span className={`text-sm font-medium ${theme.accent} block`}>
              {nft.floorPrice}
            </span>
            <a
              href={nft.openseaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-700 text-sm"
            >
              View on OpenSea
            </a>
          </div>
        </motion.div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (nfts.length === 0) {
    return (
      <div className={`text-center py-12 ${theme.subtext}`}>
        <SafeIcon icon={FiGrid} className="text-4xl mb-4 mx-auto opacity-50" />
        <p>No NFTs found in this wallet</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border ${theme.border} ${theme.bg} overflow-hidden`}
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-xl font-bold ${theme.text}`}>
            NFT Gallery
          </h3>
          <div className="flex items-center space-x-2">
            {/* View Mode Toggle */}
            <div className={`flex rounded-lg border ${theme.border}`}>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-l-lg ${viewMode === 'grid' ? 'bg-indigo-600 text-white' : `${theme.bg} ${theme.text}`}`}
              >
                <SafeIcon icon={FiGrid} className="text-sm" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-r-lg ${viewMode === 'list' ? 'bg-indigo-600 text-white' : `${theme.bg} ${theme.text}`}`}
              >
                <SafeIcon icon={FiList} className="text-sm" />
              </button>
            </div>
            <button onClick={loadNFTs} className={`p-2 rounded-lg ${theme.bg} ${theme.border} border ${theme.text} hover:opacity-80`}>
              <SafeIcon icon={FiRefreshCw} className="text-sm" />
            </button>
          </div>
        </div>

        {/* Collection Filter */}
        <div className="flex items-center space-x-2 mb-6">
          <SafeIcon icon={FiFilter} className={`${theme.subtext}`} />
          <select
            value={selectedCollection}
            onChange={(e) => setSelectedCollection(e.target.value)}
            className={`px-3 py-2 rounded-lg border ${theme.border} ${theme.bg} ${theme.text} focus:ring-2 focus:ring-indigo-500`}
          >
            <option value="all">All Collections</option>
            {collections.map(collection => (
              <option key={collection} value={collection}>{collection}</option>
            ))}
          </select>
        </div>
        
        {/* NFT Display */}
        {viewMode === 'grid' ? renderGridView() : renderListView()}
      </div>
    </motion.div>
  );
};

export default NFTGallery;